import { inject } from '@angular/core';
import { Route, Routes } from '@angular/router';
import { AppLayoutComponent } from '@common/_layout/app-layout/app-layout.component';
import { AuthCallbackComponent } from '@common/components/login/auth-callback/auth-callback.component';
import { ProviderPath, RoutePath, ServiceType } from '@common/enums';
import {
  authGuard,
  authMatchGuard,
  autoRedirectToAuthUserGuard,
  availableServicesGuard,
  dataChangeWatcherGuard,
  notDomainUserGuard
} from '@common/guards';
import { PageData } from '@common/models';
import { I18NEXT_NAMESPACE_RESOLVER, I18NextPipe } from 'angular-i18next';
import { I18_NAMESPACE_APPS_UI } from 'i18n';

const i18nTitle = (key: string) => () => inject(I18NextPipe).transform(key, { format: 'capitalize' });

const pageRoute = ({ i18nextNamespaces, serviceType, ...route }: Route & PageData): Route => ({
  ...route,
  data: { i18nextNamespaces, ...(serviceType != null && { serviceType }) } as PageData,
  resolve: { i18next: I18NEXT_NAMESPACE_RESOLVER }
});

const defaultRoutes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: RoutePath.Dashboard
  },
  pageRoute({
    path: RoutePath.AuditLog,
    canActivate: [notDomainUserGuard],
    title: i18nTitle('page.auditLog'),
    loadChildren: () => import('@pages/audit-log/audit-log.routes').then((f) => f.ROUTES),
    i18nextNamespaces: [I18_NAMESPACE_APPS_UI.audit, I18_NAMESPACE_APPS_UI.policy, I18_NAMESPACE_APPS_UI.reports]
  }),
  pageRoute({
    path: RoutePath.Dashboard,
    title: i18nTitle('page.dashboard'),
    loadChildren: () => import('@pages/dashboard/dashboard.routes').then((f) => f.ROUTES),
    i18nextNamespaces: [I18_NAMESPACE_APPS_UI.chart, I18_NAMESPACE_APPS_UI.dashboard]
  }),
  pageRoute({
    path: RoutePath.Users,
    canActivate: [availableServicesGuard],
    canDeactivate: [dataChangeWatcherGuard],
    title: i18nTitle('page.users'),
    loadChildren: () => import('@pages/users/users.routes').then((f) => f.ROUTES),
    i18nextNamespaces: [I18_NAMESPACE_APPS_UI.users, I18_NAMESPACE_APPS_UI.policy],
    serviceType: ServiceType.Users
  }),
  pageRoute({
    path: RoutePath.Payments,
    canActivate: [availableServicesGuard],
    title: i18nTitle('page.payments'),
    loadChildren: () => import('@pages/payments/payments.routes').then((f) => f.ROUTES),
    i18nextNamespaces: [I18_NAMESPACE_APPS_UI.payments],
    serviceType: ServiceType.Payments
  }),
  pageRoute({
    path: RoutePath.Mail,
    runGuardsAndResolvers: 'always',
    canActivate: [availableServicesGuard, autoRedirectToAuthUserGuard(RoutePath.Mail)],
    canDeactivate: [dataChangeWatcherGuard],
    title: i18nTitle('page.mail'),
    loadChildren: () => import('@pages/mail/mail.routes').then((f) => f.ROUTES),
    i18nextNamespaces: [I18_NAMESPACE_APPS_UI.mail, I18_NAMESPACE_APPS_UI.dontHavePermission],
    serviceType: ServiceType.Mail
  }),
  pageRoute({
    path: RoutePath.Drive,
    runGuardsAndResolvers: 'always',
    canActivate: [availableServicesGuard, autoRedirectToAuthUserGuard(RoutePath.Drive)],
    title: i18nTitle('page.drive'),
    loadChildren: () => import('@pages/drive/drive.routes').then((f) => f.ROUTES),
    i18nextNamespaces: [I18_NAMESPACE_APPS_UI.drive, I18_NAMESPACE_APPS_UI.dontHavePermission],
    serviceType: ServiceType.Drive
  }),
  pageRoute({
    path: RoutePath.History,
    title: i18nTitle('page.backupStatistics'),
    loadChildren: () => import('@pages/history/history.routes').then((f) => f.ROUTES),
    i18nextNamespaces: [I18_NAMESPACE_APPS_UI.chart, I18_NAMESPACE_APPS_UI.history]
  }),
  pageRoute({
    path: RoutePath.SharedDrives,
    canActivate: [availableServicesGuard],
    title: i18nTitle('page.sharedDrives'),
    loadChildren: () => import('@pages/team-drive/team-drive.routes').then((f) => f.ROUTES),
    i18nextNamespaces: [I18_NAMESPACE_APPS_UI.drive, I18_NAMESPACE_APPS_UI.dontHavePermission],
    serviceType: ServiceType.TeamDrives
  }),
  pageRoute({
    path: RoutePath.Sharepoint,
    canActivate: [availableServicesGuard],
    title: i18nTitle('page.sharePoint'),
    loadChildren: () => import('@pages/share-point/share-point.routes').then((f) => f.ROUTES),
    i18nextNamespaces: [I18_NAMESPACE_APPS_UI.drive, I18_NAMESPACE_APPS_UI.dontHavePermission],
    serviceType: ServiceType.SharePoint
  }),
  pageRoute({
    path: RoutePath.Teams,
    canActivate: [availableServicesGuard],
    title: i18nTitle('page.teams'),
    loadChildren: () => import('@pages/teams/teams.routes').then((f) => f.ROUTES),
    i18nextNamespaces: [I18_NAMESPACE_APPS_UI.drive, I18_NAMESPACE_APPS_UI.dontHavePermission],
    serviceType: ServiceType.Teams
  }),
  pageRoute({
    path: RoutePath.Contacts,
    runGuardsAndResolvers: 'always',
    canActivate: [availableServicesGuard, autoRedirectToAuthUserGuard(RoutePath.Contacts)],
    title: i18nTitle('page.contacts'),
    loadChildren: () => import('@pages/contacts/contacts.routes').then((f) => f.ROUTES),
    i18nextNamespaces: [I18_NAMESPACE_APPS_UI.contacts, I18_NAMESPACE_APPS_UI.dontHavePermission],
    serviceType: ServiceType.Contacts
  }),
  pageRoute({
    path: RoutePath.Calendar,
    runGuardsAndResolvers: 'always',
    canActivate: [availableServicesGuard, autoRedirectToAuthUserGuard(RoutePath.Calendar)],
    title: i18nTitle('page.calendar'),
    loadChildren: () => import('@pages/calendar/calendar.routes').then((f) => f.ROUTES),
    i18nextNamespaces: [I18_NAMESPACE_APPS_UI.calendar, I18_NAMESPACE_APPS_UI.dontHavePermission],
    serviceType: ServiceType.Calendar
  }),
  pageRoute({
    path: RoutePath.RetentionPolicy,
    canActivate: [notDomainUserGuard],
    title: i18nTitle('page.retentionPolicy'),
    loadChildren: () => import('@pages/retention-policy/retention-policy.routes').then((f) => f.ROUTES),
    i18nextNamespaces: [I18_NAMESPACE_APPS_UI.policy]
  }),
  pageRoute({
    path: RoutePath.TaskManager,
    title: i18nTitle('page.taskManager'),
    loadChildren: () => import('@pages/task-manager/task-manager.routes').then((f) => f.ROUTES),
    i18nextNamespaces: [I18_NAMESPACE_APPS_UI.tasks]
  }),
  pageRoute({
    path: RoutePath.ExportResults,
    title: i18nTitle('page.exportResults'),
    loadChildren: () => import('@pages/export-results/export-results.routes').then((f) => f.ROUTES),
    i18nextNamespaces: [I18_NAMESPACE_APPS_UI.results],
    serviceType: ServiceType.Export
  }),
  pageRoute({
    path: RoutePath.Reports,
    canActivate: [notDomainUserGuard],
    canDeactivate: [dataChangeWatcherGuard],
    title: i18nTitle('page.reports'),
    loadChildren: () => import('@pages/reports/reports.routes').then((f) => f.ROUTES),
    i18nextNamespaces: [I18_NAMESPACE_APPS_UI.reports]
  }),
  pageRoute({
    path: RoutePath.BackupHistory,
    canActivate: [notDomainUserGuard],
    title: i18nTitle('page.backupHistory'),
    loadChildren: () => import('@pages/backup-history/backup-history.routes').then((f) => f.ROUTES),
    i18nextNamespaces: [I18_NAMESPACE_APPS_UI.backupHistory]
  }),
  pageRoute({
    path: RoutePath.SkipItems,
    canActivate: [notDomainUserGuard],
    title: i18nTitle('page.skippedItems'),
    loadChildren: () => import('@pages/skip-items/skip-items.routes').then((f) => f.ROUTES),
    i18nextNamespaces: [I18_NAMESPACE_APPS_UI.skipItems]
  }),
  {
    path: RoutePath.Help,
    title: i18nTitle('page.help'),
    loadChildren: () => import('@pages/help/help.routes').then((f) => f.ROUTES)
  },
  {
    path: RoutePath.NotFound,
    title: i18nTitle('page.notFound'),
    loadComponent: () => import('@pages/not-found/component/not-found.component').then((c) => c.NotFoundComponent)
  }
];

const getSubsystemRoute = (path: string): Route => ({
  path: path,
  children: [
    {
      path: RoutePath.AuthCallback,
      title: i18nTitle('page.authCallback'),
      component: AuthCallbackComponent
    },
    {
      path: '',
      canMatch: [authMatchGuard],
      canActivate: [authGuard],
      component: AppLayoutComponent,
      children: defaultRoutes
    },
    {
      path: '**',
      redirectTo: `/${path}/${RoutePath.NotFound}`
    }
  ]
});

export const ROUTES: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: RoutePath.Login
  },
  {
    path: RoutePath.PublicHelp,
    title: i18nTitle('page.help'),
    loadComponent: () => import('@common/components/public/help/public-help.component').then((c) => c.PublicHelpComponent),
    resolve: { i18next: I18NEXT_NAMESPACE_RESOLVER }
  },
  {
    path: RoutePath.Account,
    title: i18nTitle('page.account'),
    loadComponent: () => import('@pages/account/component/account.component').then((c) => c.AccountComponent),
    resolve: { i18next: I18NEXT_NAMESPACE_RESOLVER }
  },
  {
    path: RoutePath.Login,
    title: i18nTitle('page.login'),
    loadComponent: () => import('@common/components/login/login.component').then((c) => c.LoginComponent),
    resolve: { i18next: I18NEXT_NAMESPACE_RESOLVER }
  },
  getSubsystemRoute(ProviderPath.Office),
  getSubsystemRoute(ProviderPath.Google),
  getSubsystemRoute(ProviderPath.Alternate),
  {
    path: '**',
    redirectTo: RoutePath.Login
  }
];
